/*
 * @Author: mina.min
 * @Date: 2025-01-20 10:00:00
 * @LastEditors: mina.min
 * @LastEditTime: 2025-06-17 17:57:33
 * @FilePath: /OTAManagementSystem/src/store/moduleStore.ts
 * @Description: 模块管理数据存储
 */

import {
  deleteModule as deleteModuleApi,
  editModule,
  EditModuleRequest,
  getModuleDetail as fetchModuleDetail,
  getModuleList as fetchModuleList,
  ModuleItem,
  saveModule,
  SaveModuleRequest,
} from '@/services/module'
import { action, makeAutoObservable, runInAction } from 'mobx'

// API 响应类型
interface ApiResponse<T> {
  code: string
  info: string
  data: T
}

// 分页数据类型
interface PaginationData {
  pageNum: number
  pageSize: number
  total: number
}

// 模块信息类型
export interface ModuleInfo {
  id: string
  moduleName: string
  remark: string
  status: 'START' | 'DISABLED'
  hardwareId?: number
  createdTime: string
  lastUpdated: string
}

// 分页配置类型
interface PaginationConfig {
  current: number
  pageSize: number
  total: number
  showSizeChanger: boolean
  showQuickJumper: boolean
  showTotal: (total: number) => string
  pageSizeOptions: string[]
}

class ModuleStore {
  // 状态
  moduleList: ModuleInfo[] = []
  loading: boolean = false
  pagination: PaginationConfig = {
    current: 1,
    pageSize: 50,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total: number) => `共 ${total} 条记录`,
    pageSizeOptions: ['50', '100'],
  }

  constructor() {
    makeAutoObservable(this)
  }

  @action.bound
  async getModuleList(
    page = 1,
    pageSize = 50
  ): Promise<ApiResponse<{ list: ModuleInfo[] } & PaginationData>> {
    try {
      this.loading = true
      const response = await fetchModuleList(page, pageSize)
      if (String(response.code) === '200') {
        // 转换数据格式
        const formattedList = response.data.list.map(
          (item: ModuleItem): ModuleInfo => ({
            id: item.moduleId,
            moduleName: item.moduleName,
            remark: item.remark,
            status: item.status === 'START' ? 'START' : 'DISABLED',
            hardwareId: item.hardwareId,
            createdTime: item.createTime,
            lastUpdated: item.updateTime,
          })
        )

        runInAction(() => {
          this.moduleList = formattedList
          this.pagination = {
            ...this.pagination,
            current: response.data.pageNum,
            pageSize: response.data.pageSize,
            total: response.data.total,
          }
        })

        return {
          code: '200',
          info: '获取成功',
          data: {
            list: formattedList,
            pageNum: response.data.pageNum,
            pageSize: response.data.pageSize,
            total: response.data.total,
          },
        }
      } else {
        throw new Error(response.info || '获取模块列表失败')
      }
    } catch (error) {
      console.error('获取模块列表失败:', error)
      throw error
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound
  async addModule(module: Omit<ModuleInfo, 'id' | 'createdTime' | 'lastUpdated'>) {
    try {
      runInAction(() => {
        this.loading = true
      })
      const requestData: SaveModuleRequest = {
        moduleName: module.moduleName,
        remark: module.remark,
        status: module.status === 'START' ? 'START' : 'DISABLED',
        hardwareId: module.hardwareId,
      }
      const response = await saveModule(requestData)
      if (response.code === '200') {
        await this.getModuleList() // 刷新列表
      } else {
        throw new Error(response.info || '添加模块失败')
      }
    } catch (error) {
      console.error('添加模块失败:', error)
      throw error
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound
  async updateModule(id: string, module: Partial<ModuleInfo>) {
    try {
      runInAction(() => {
        this.loading = true
      })
      const requestData: EditModuleRequest = {
        moduleId: id,
        moduleName: module.moduleName!,
        hardwareId: module.hardwareId,
        remark: module.remark!,
        status: module.status as 'START' | 'DISABLED',
      }
      const response = await editModule(requestData)
      if (response.code === '200') {
        await this.getModuleList() // 刷新列表
      } else {
        throw new Error(response.info || '编辑模块失败')
      }
    } catch (error) {
      console.error('编辑模块失败:', error)
      throw error
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound
  async deleteModule(id: string) {
    try {
      runInAction(() => {
        this.loading = true
      })
      const response = await deleteModuleApi({ moduleId: id })
      if (response.code === '200') {
        await this.getModuleList() // 刷新列表
      } else {
        throw new Error(response.info || '删除模块失败')
      }
    } catch (error) {
      console.error('删除模块失败:', error)
      throw error
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }

  @action.bound
  async getModuleDetail(moduleId: string): Promise<ModuleInfo | null> {
    try {
      runInAction(() => {
        this.loading = true
      })
      const response = await fetchModuleDetail(moduleId)
      if (response.code === '200') {
        const moduleInfo: ModuleInfo = {
          id: response.data.moduleId,
          moduleName: response.data.moduleName,
          remark: response.data.remark,
          status: response.data.status === 'START' ? 'START' : 'DISABLED',
          hardwareId: response.data.hardwareId,
          createdTime: response.data.createTime,
          lastUpdated: response.data.updateTime,
        }
        return moduleInfo
      } else {
        throw new Error(response.info || '获取模块详情失败')
      }
    } catch (error) {
      console.error('获取模块详情失败:', error)
      throw error
    } finally {
      runInAction(() => {
        this.loading = false
      })
    }
  }
}

export const moduleStore = new ModuleStore()